import { FC, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import parse, { HTMLReactParserOptions, Element } from 'html-react-parser';
import Button from '@ingka/button';
import ToggleControlWrapper from '@ingka/toggle';
import Tooltip from '@ingka/tooltip';
import downloadIcon from '@ingka/ssr-icon/paths/arrow-down-to-line-small';
import copyIcon from '@ingka/ssr-icon/paths/copy';
import InformationIcon from '@ingka/ssr-icon/paths/information';
import { useToast } from '@pcmp/react-providers';

import { CodeV2 as Code } from '@/redux/apis/codes-api';
import { saveSvgAsPng, saveSvgAsSvg } from '@/lib/utils/file';
import { Data } from '@pcmp/react-coworker-components';
import { useCode } from '../../create-code/context';
import { useGetImageLink } from '@/lib/hooks/qrcode';
import { useNavigate } from 'react-router-dom';
import { useGetV1QrCodesBySlugAndNameExtQuery } from '@/redux/apis/images-api/generated';

const { Card } = Data;

type DownloadFormat = 'SVG' | 'PNG';
type FileExtension = 'png' | 'svg';

const DOWNLOAD_FORMATS: DownloadFormat[] = ['PNG', 'SVG'];
const DOWNLOAD_FORMATS_HANDLERS: Record<
  DownloadFormat,
  (svg: SVGElement, filename: string) => void
> = {
  PNG: saveSvgAsPng,
  SVG: saveSvgAsSvg,
};

interface SvgComponentProps {
  imageData: string;
}

export const SvgComponent: FC<SvgComponentProps> = ({ imageData }) => {
  if (!imageData) return null;
  // Transform to inject class into the <svg> tag
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'svg') {
        // Add Tailwind classes
        domNode.attribs = {
          ...domNode.attribs,
          className: 'w-full h-full min-h-100 min-w-100',
        };
      }
    },
  };

  return <>{parse(imageData, options)}</>;
};

const CodeRenderer: FC<{ code: Code }> = ({ code }) => {
  const { t } = useTranslation();
  const getImageLink = useGetImageLink();
  const svgWrapperRef = useRef<HTMLDivElement>(null);
  const [formatIndex, setFormatIndex] = useState(0);
  const format = DOWNLOAD_FORMATS[formatIndex];
  const { pushToast } = useToast();

  const imageName = encodeURIComponent(`${code.codeName}-${code.icon}`); // We concatenate the icon to ensure a new image is fetched when the icon changes and avoid potential caching issues
  const { data: imageData } = useGetV1QrCodesBySlugAndNameExtQuery({
    slug: code.slug!,
    name: imageName,
    ext: 'svg',
  });

  const downloadImage = () => {
    const svg = svgWrapperRef.current?.children[0] as SVGElement;
    if (svg) {
      DOWNLOAD_FORMATS_HANDLERS[format](svg, code.codeName);
    }
  };

  const copyImage = async () => {
    let status;
    const fileExtension: FileExtension = format === 'PNG' ? 'png' : 'svg';

    try {
      const newQrImageUrl = getImageLink(code, fileExtension);
      await navigator.clipboard.writeText(newQrImageUrl);

      status = 'success';
    } catch (error) {
      status = 'error';
    }
    const message = t(`page.codes.detail.messages.copyImageUrl.${status}`);
    // @ts-ignore
    pushToast({ message });
  };

  return (
    <div className="flex flex-col sm:flex-row sm:h-[100px] gap-5 sm:gap-0 items-center">
      <div className="w-[100px] h-full" ref={svgWrapperRef}>
        <SvgComponent imageData={(imageData as string | undefined) || ''} />
      </div>

      <div className="sm:pl-8 flex flex-col flex-grow h-full justify-between gap-5 sm:gap-0">
        <div className="flex h-[30px] items-center gap-[var(--space-125,20px)] self-stretch">
          <ToggleControlWrapper
            className="flex-grow"
            data-testid="downloadFormatToggle"
            onClick={(_, index) => setFormatIndex(index)}
            activeIndex={formatIndex}
            buttons={DOWNLOAD_FORMATS.map((format) => ({ text: format }))}
            fluid
          />
          <Tooltip
            position="top"
            ssrIcon={InformationIcon}
            // @ts-ignore
            tooltipText={
              <Trans i18nKey="page.codes.detail.tooltips.formatsInformation" />
            }
          />
        </div>

        <div className="flex items-start gap-5 ">
          <Button
            className="grow-[2] min-w-[119px]"
            data-testid="downloadImageButton"
            size="small"
            type="primary"
            iconPosition="leading"
            ssrIcon={downloadIcon}
            text={t('label.download')}
            onClick={downloadImage}
          />
          <Button
            className="grow-[1] min-w-[150px] tutorial__copy-image-url--step1"
            data-testid="copyImageButton"
            size="small"
            type="secondary"
            iconPosition="leading"
            ssrIcon={copyIcon}
            text={t('label.copyUrl')}
            onClick={copyImage}
            title={t('page.codes.detail.tooltips.copyUrl')}
          />
        </div>
      </div>
    </div>
  );
};

const CodeStatistics: FC<{ code: Code }> = ({ code }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-row gap-4 items-center">
      <div className="bg-sky-700 h-[100px] min-w-[100px] rounded-full flex flex-col items-center justify-center text-white">
        <span className="text-4xl">{code.scans}</span>
        <span className="max-w-[56px] truncate text-center">
          {t('label.code.scans').toLocaleLowerCase()}
        </span>
      </div>
      <Button
        data-testid="viewStatsButton"
        className="grow-[1]"
        size="medium"
        text={t('page.codes.detail.viewStats')}
        onClick={() => navigate(`/stats/${code.slug!}`)}
      />
    </div>
  );
};

export const PreviewQrCode: FC = () => {
  const { code } = useCode();
  return (
    <div className="flex flex-col md:flex-row gap-4">
      <Card className="w-full md:w-3/5 md:min-w-[500px]">
        <CodeRenderer code={code} />
      </Card>
      <Card className="w-full md:w-2/5 md:min-w-[350px]">
        <CodeStatistics code={code} />
      </Card>
    </div>
  );
};
